import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Image,
} from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { ShieldCheck, ChevronLeft } from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { useAlert } from '@/components/AlertDialog';
import { AuthLayout } from '@/components/AuthLayout';
import { isWeb } from '@/lib/platform';
import { useAuth, isSupabaseConfigured, supabase } from '@/lib/supabase';
import { AAL_QUERY_KEY } from '@/lib/auth/aal';
import { getUserMessage, reportError } from '@/lib/errors';
import { useProfileStore } from '@/lib/state/profile-store';
import { useSubscriptionStore } from '@/lib/state/subscription-store';

import { LOGO_MARK } from '@/lib/logo';

const CODE_LENGTH = 6;

/** Roles this screen can route to after a successful step-up. */
type AppDestinationRole = 'startup' | 'investor' | 'admin';

export default function MFAVerifyScreen() {
  const router = useRouter();
  const colors = useSystemThemeColors();
  const params = useLocalSearchParams<{ factorId?: string; challengeId?: string; role?: string }>();
  const { alert, error: showError } = useAlert();
  const { verifyMFA, isConfigured, profile, signOut } = useAuth();
  const queryClient = useQueryClient();

  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef<(TextInput | null)[]>([]);

  // A challenge this screen created for itself.
  //
  // WHY: the route-group guards now redirect an enrolled-but-AAL1 session here
  // (SEC-AUTH-02c), and they have no factorId/challengeId to pass — the guard
  // only knows the session is too weak. Without self-initialisation the screen
  // would render "Missing verification parameters", the user could not step up,
  // and the guard would redirect them straight back: an inescapable loop.
  //
  // The login flow still passes both params, so that path is unchanged and does
  // not pay for a second challenge.
  const [selfChallenge, setSelfChallenge] = useState<{ factorId: string; challengeId: string } | null>(null);
  const [initError, setInitError] = useState<string | null>(null);

  const factorId = params.factorId || selfChallenge?.factorId;
  const challengeId = params.challengeId || selfChallenge?.challengeId;

  useEffect(() => {
    // Only when we arrived WITHOUT a challenge (i.e. redirected by a guard).
    if (params.factorId && params.challengeId) return;
    if (selfChallenge || !isConfigured || !isSupabaseConfigured()) return;

    let cancelled = false;
    (async () => {
      try {
        const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
        if (listError) throw listError;
        const verified = factors?.totp?.filter((f) => f.status === 'verified') ?? [];
        if (verified.length === 0) {
          // Enrolled-but-no-verified-factor is an inconsistent state we must not
          // trap the user in; sign them out so they can start cleanly.
          if (!cancelled) setInitError('No verified authenticator found for this account.');
          return;
        }
        const { data: challenge, error: challengeError } =
          await supabase.auth.mfa.challenge({ factorId: verified[0].id });
        if (challengeError) throw challengeError;
        if (!cancelled) setSelfChallenge({ factorId: verified[0].id, challengeId: challenge.id });
      } catch (err) {
        reportError(err, { context: 'MFAVerifyScreen.initChallenge', level: 'warning', silent: true });
        if (!cancelled) setInitError('Could not start verification. Please sign in again.');
      }
    })();

    return () => { cancelled = true; };
  }, [params.factorId, params.challengeId, selfChallenge, isConfigured]);

  // Store actions
  const setUserRole = useProfileStore((s) => s.setUserRole);
  const setSubscriptionUserRole = useSubscriptionStore((s) => s.setUserRole);

  // Focus first input on mount
  useEffect(() => {
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
  }, []);

  const handleCodeChange = (value: string, index: number) => {
    // Only allow digits
    const digit = value.replace(/[^0-9]/g, '');

    if (digit.length <= 1) {
      const newCode = [...code];
      newCode[index] = digit;
      setCode(newCode);

      // Auto-focus next input
      if (digit && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      // Auto-submit when all digits entered
      if (digit && index === CODE_LENGTH - 1) {
        const fullCode = newCode.join('');
        if (fullCode.length === CODE_LENGTH) {
          handleVerify(fullCode);
        }
      }
    } else if (digit.length === CODE_LENGTH) {
      // Handle paste
      const digits = digit.split('');
      setCode(digits);
      handleVerify(digit);
    }
  };

  const handleKeyPress = (key: string, index: number) => {
    if (key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const verifyMutation = useMutation({
    mutationFn: async (codeToVerify: string) => {
      if (!factorId || !challengeId) {
        throw new Error('Missing verification parameters. Please try signing in again.');
      }
      if (isConfigured && isSupabaseConfigured()) {
        const { error } = await verifyMFA(factorId, challengeId, codeToVerify);
        if (error) throw error;
        return (params.role || profile?.role || 'investor') as AppDestinationRole;
      } else {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return (params.role || 'investor') as AppDestinationRole;
      }
    },
    onSuccess: async (userRole) => {
      // The session is now AAL2. The route guards read that through the cached
      // ['auth-aal2-state'] query, so it MUST be invalidated before we navigate
      // — otherwise the guard re-reads a stale 'step-up-required' and bounces
      // the user straight back here.
      await queryClient.invalidateQueries({ queryKey: AAL_QUERY_KEY });

      // Admin is a real role here: an admin who steps up must land in the admin
      // shell, not be silently routed to the investor dashboard.
      if (userRole !== 'admin') {
        setUserRole(userRole);
        setSubscriptionUserRole(userRole);
      }
      router.replace(
        userRole === 'admin'
          ? '/(admin)/admin-dashboard'
          : userRole === 'startup'
          ? '/(startup)/startup-dashboard'
          : '/(investor)/investor-dashboard'
      );
    },
    onError: (error: any) => {
      if (error.message?.includes('Missing verification')) {
        showError('Verification Failed', getUserMessage(error));
      } else {
        showError('Verification Failed', getUserMessage(error));
        setCode(Array(CODE_LENGTH).fill(''));
        inputRefs.current[0]?.focus();
      }
    },
  });

  const isLoading = verifyMutation.isPending;

  const handleVerify = (fullCode?: string) => {
    const codeToVerify = fullCode || code.join('');
    if (codeToVerify.length !== CODE_LENGTH) {
      alert('Incomplete Code', 'Please enter the full 6-digit code.');
      return;
    }
    verifyMutation.mutate(codeToVerify);
  };

  const handleResendCode = async () => {
    // In a real implementation, this would request a new challenge
    alert('Info', 'Use your authenticator app to get a new code. The code refreshes every 30 seconds.');
  };

  // Abandoning the step-up must DISCARD the session, not just navigate away.
  //
  // The user still holds a live AAL1 session at this point. Navigating to
  // /login with that session intact would have the login screen see a signed-in
  // user, redirect it back into a route group, and the guard would send it
  // straight back here — an inescapable loop. Signing out is also the correct
  // security outcome: an unfinished MFA login is not a login.
  const handleBackToLogin = async () => {
    try {
      await signOut();
    } catch (err) {
      reportError(err, { context: 'MFAVerifyScreen.handleBackToLogin', level: 'warning', silent: true });
    }
    router.replace('/login');
  };

  return (
    <AuthLayout>
      <View className="flex-1" style={{ backgroundColor: isWeb ? colors.surface.primary : colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top', 'bottom']} className="flex-1">
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            className="flex-1"
          >
            <ScrollView
              className="flex-1"
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ flexGrow: 1, justifyContent: isWeb ? 'center' : 'flex-start' }}
              keyboardShouldPersistTaps="handled"
            >
              {/* Header - hide on web */}
              {!isWeb && (
                <Animated.View
                  entering={FadeInDown.delay(100).duration(500)}
                  className="px-5 pt-4"
                >
                  <AnimatedPressable
                    onPress={handleBackToLogin}
                    className="w-10 h-10 rounded-full items-center justify-center"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <ChevronLeft size={24} color={colors.text.primary} />
                  </AnimatedPressable>
                </Animated.View>
              )}

              {/* Icon & Title */}
              <Animated.View
                entering={FadeInDown.delay(200).duration(500)}
                className={`items-center px-5 ${isWeb ? 'pt-4 pb-6' : 'pt-8 pb-8'}`}
              >
                {!isWeb && (
                  <Image
                    source={LOGO_MARK}
                    className="w-16 h-16 mb-4"
                    resizeMode="contain"
                  />
                )}
                <View
                  className="w-16 h-16 rounded-full items-center justify-center mb-4"
                  style={{ backgroundColor: colors.primary[50] }}
                >
                  <ShieldCheck size={32} color={colors.primary[600]} />
                </View>
                <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mb-2">
                  Two-Factor Authentication
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-center text-base leading-relaxed px-4">
                  {initError ?? 'Enter the 6-digit code from your authenticator app.'}
                </Text>
              </Animated.View>

              {/* Code Input */}
              <Animated.View
                entering={FadeInUp.delay(300).duration(500)}
                className="px-5"
              >
                <View className="flex-row justify-center gap-2">
                  {code.map((digit, index) => (
                    <TextInput
                      key={index}
                      ref={(ref) => { inputRefs.current[index] = ref; }}
                      style={{
                        color: colors.text.primary,
                        backgroundColor: colors.surface.primary,
                        borderWidth: 2,
                        borderColor: digit ? colors.primary[500] : colors.border.light,
                      }}
                      className="w-12 h-14 text-center text-xl font-bold rounded-xl"
                      value={digit}
                      onChangeText={(value) => handleCodeChange(value, index)}
                      onKeyPress={({ nativeEvent }) => handleKeyPress(nativeEvent.key, index)}
                      keyboardType="number-pad"
                      maxLength={CODE_LENGTH}
                      selectTextOnFocus
                    />
                  ))}
                </View>
              </Animated.View>

              {/* Verify Button */}
              <Animated.View
                entering={FadeInUp.delay(400).duration(500)}
                className="px-5 mt-8"
              >
                <AnimatedPressable
                  onPress={() => handleVerify()}
                  disabled={isLoading || code.some((d) => !d)}
                  className="rounded-xl py-4 flex-row items-center justify-center gap-2"
                  style={{
                    backgroundColor: isLoading || code.some((d) => !d)
                      ? colors.border.medium
                      : colors.primary[600],
                  }}
                >
                  <Text style={{ color: colors.text.inverse }} className="font-semibold text-base">
                    {isLoading ? 'Verifying...' : 'Verify'}
                  </Text>
                </AnimatedPressable>
              </Animated.View>

              {/* Help Text */}
              <Animated.View
                entering={FadeInUp.delay(500).duration(500)}
                className="px-5 mt-6"
              >
                <View
                  className="rounded-xl p-4"
                  style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                >
                  <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed text-center">
                    Open your authenticator app (Google Authenticator, Authy, etc.) and enter the current code for Inverio.
                  </Text>
                </View>
              </Animated.View>

              {/* Trouble signing in */}
              <Animated.View
                entering={FadeInUp.delay(600).duration(500)}
                className="items-center px-5 mt-6 pb-8"
              >
                <Pressable onPress={handleResendCode}>
                  <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                    Having trouble?
                  </Text>
                </Pressable>
                {/* Always-available escape hatch. A user who cannot produce a
                    code (lost device) must be able to leave — and leaving signs
                    them out, so no AAL1 session is left behind. */}
                <Pressable onPress={handleBackToLogin} className="mt-4">
                  <Text style={{ color: colors.text.tertiary }} className="text-sm">
                    Sign in with a different account
                  </Text>
                </Pressable>
              </Animated.View>
            </ScrollView>
          </KeyboardAvoidingView>
        </SafeAreaView>
      </View>
    </AuthLayout>
  );
}
